// Product Cards
if (!customElements.get('product-card')) {
  customElements.define(
    'product-card',
    class ProductCard extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
      }

      connectedCallback() {
        this.addEventListeners();
        this.initBazaarvoice();
        this.initSwatchesSlider();
      }

      addEventListeners() {
        // Hover event
        const hoverElem = this.querySelector('[data-product-card-inner]');
        if (hoverElem) {
          hoverElem.removeEventListener('mouseenter', this.handleCardHover);
          hoverElem.addEventListener('mouseenter', this.handleCardHover, { once: true });
        }

        // Swatches click events
        const cardSwatches = this.querySelectorAll('[data-product-card-color-id]');
        cardSwatches.forEach((swatch) => {
          swatch.removeEventListener('click', this.handleSwatchClick);
          swatch.addEventListener('click', this.handleSwatchClick);
        });

        // Show remaining colours event
        if (this.querySelector('[data-show-remaining-colours]')) {
          this.querySelector('[data-show-remaining-colours]').addEventListener('click', (e) => {
            e.target.remove();

            this.querySelectorAll('.product-card-colors__link--hidden').forEach((el) => {
              el.classList.remove('product-card-colors__link--hidden');
            });
          });
        }
      }

      /**
       * Handle card hover
       * @description Handles the hover event for product cards.
       */
      async handleCardHover(event) {
        const elem = event.currentTarget;
        const card = elem.closest('[data-product-card]');
        const productHandle = card?.dataset?.handle;
        const loadingClass = 'product-card--is-loading';

        if (!productHandle) return;

        try {
          card.classList.add(loadingClass);
          const additionalProductData = await fetchAdditionalProductData(productHandle);

          if (!additionalProductData) {
            throw new Error('No additionalProductData returned');
          }

          productCardDomChange(card, additionalProductData);
        } catch (error) {
          console.error('Error getting additional product data:', error);
        } finally {
          card.classList.remove(loadingClass);
        }
      }

      /**
       * Handle swatch click
       * @description Handles the click event for product card color swatches.
       */
      async handleSwatchClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const elem = event.currentTarget;
        const card = elem.closest('[data-product-card]');
        const swatchProductHandle = elem.dataset?.productCardColorId;
        const loadingClass = 'product-card--is-loading';

        if (!swatchProductHandle) return;

        try {
          card.classList.add(loadingClass);
          const additionalProductData = await fetchAdditionalProductData(swatchProductHandle);

          if (!additionalProductData) {
            throw new Error('No additionalProductData returned');
          }

          const cardSwatches = elem.closest('[data-product-card]').querySelectorAll('[data-product-card-color-id]');
          const activeSwatchClass = 'is-active';

          cardSwatches.forEach((cardSwatch) => cardSwatch.classList.remove(activeSwatchClass));
          elem.classList.add(activeSwatchClass);

          productCardDomChange(card, additionalProductData);
        } catch (error) {
          console.error('Error getting additional product data:', error);
        } finally {
          card.classList.remove(loadingClass);
        }
      }

      /**
       * Mutation observer for Bazaarvoice ratings
       */
      initBazaarvoice() {
        // Function to resize SVGs within a given container
        const resizeBazaarvoiceSVGs = (container) => {
          const svgs = container.querySelectorAll('svg');
          svgs.forEach((svg) => {
            svg.style.setProperty('width', '12px', 'important');
            svg.style.setProperty('height', '12px', 'important');
          });

          const mainContainer = container.closest('.bv_main_container');
          if (mainContainer) {
            // Find the element holding the number of reviews
            const numReviewsEl = mainContainer.querySelector('.bv_numReviews_component_container .bv_text');

            // Check if the review count is strictly "(0)"
            if (numReviewsEl && numReviewsEl.textContent.trim() === '(0)') {
              const subContainer = mainContainer.querySelector('.bv_sub_container');
              const starsContainer = mainContainer.querySelector('.bv_stars_component_container');

              // Update the text container to just say "No reviews"
              if (subContainer) {
                subContainer.innerHTML = `<div class="bv_text">${window.variantStrings.noReviews || 'No reviews'}</div>`;
              }

              // Hide the empty stars entirely (Optional, but usually looks best!)
              if (starsContainer) {
                starsContainer.style.display = 'none';
              }
            }
          }
          if (this.observer) this.observer.disconnect();
        };

        // Process any containers that might already be in the DOM
        const existingContainers = document.querySelectorAll('.bv_stars_button_container');
        existingContainers.forEach(resizeBazaarvoiceSVGs);

        // If observer already exists, return
        if (this.observer) return;

        // Set up the MutationObserver to watch for newly added containers
        this.observer = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                // Ensure the node is an element (not a text node or comment)
                if (node.nodeType === Node.ELEMENT_NODE) {
                  // Case A: The added node itself is the container
                  if (node.classList.contains('bv_stars_button_container')) {
                    resizeBazaarvoiceSVGs(node);
                  }

                  // Case B: The container is a child of the added node
                  else {
                    const nestedContainers = node.querySelectorAll('.bv_stars_button_container');
                    nestedContainers.forEach((node) => resizeBazaarvoiceSVGs(node));
                  }
                }
              });
            }
          }
        });

        // Start observing the document body for injected nodes
        this.observer.observe(this, {
          childList: true,
          subtree: true,
        });
      }

      /**
       * Initialize swatches slider
       * @description Handles the scroll and click events for the swatches slider.
       */
      initSwatchesSlider() {
        const swatchesContainer = this.querySelector('[data-product-card-swatches]');
        if (!swatchesContainer) return;

        const track = swatchesContainer.querySelector('.product-card-swatches__track');
        const prevButton = swatchesContainer.querySelector('[data-prev]');
        const nextButton = swatchesContainer.querySelector('[data-next]');

        if (!track || !prevButton || !nextButton) return;

        const updateButtons = () => {
          const isAtStart = track.scrollLeft <= 5;
          const isAtEnd = track.scrollLeft + track.offsetWidth >= track.scrollWidth - 5;

          prevButton.classList.toggle('product-card-swatches__nav--visible', !isAtStart);
          nextButton.classList.toggle('product-card-swatches__nav--visible', !isAtEnd);
        };

        const scrollBySwatch = (direction) => {
          const firstSwatch = track.querySelector('.product-card-swatches__link');
          if (!firstSwatch) return;

          const swatchWidth = firstSwatch.offsetWidth;
          const scrollAmount = direction === 'next' ? swatchWidth : -swatchWidth;

          track.scrollBy({
            left: scrollAmount,
            behavior: 'smooth',
          });
        };

        track.addEventListener('scroll', updateButtons);
        prevButton.addEventListener('click', (e) => {
          e.preventDefault();
          scrollBySwatch('prev');
        });
        nextButton.addEventListener('click', (e) => {
          e.preventDefault();
          scrollBySwatch('next');
        });

        // Initial update
        updateButtons();
      }
    },
  );
}
